import { useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useInView } from "framer-motion";
import CustomButton from "../../shared/custom-button/custom-button";
import NumberTicker from "../../shared/number-ticker/NumberTicker";
import "./serviceHero.scss";

const ServiceHero = ({ data }) => {
  const { title, shortIntro, serviceOverview, heroBanner } = data || {};

  const [formData, setFormData] = useState({
    Name: "",
    Email: "",
    Phone: "",
    Designation: "",
    Organization: "",
    Message: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const formRef = useRef(null);

  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, { once: true, margin: "0px 0px -100px 0px" });

  const validate = () => {
    const newErrors = {};
    if (!formData.Name.trim()) newErrors.Name = "Name is required";
    if (!formData.Email.trim()) {
      newErrors.Email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.Email)) {
      newErrors.Email = "Email is invalid";
    }
    if (!formData.Phone.trim()) {
      newErrors.Phone = "Phone number is required";
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.Phone)) {
      newErrors.Phone = "Phone number is invalid";
    }
    if (!formData.Designation.trim())
      newErrors.Designation = "Designation is required";
    if (!formData.Organization.trim())
      newErrors.Organization = "Organization name is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    fetch(
      "https://script.google.com/macros/s/AKfycbwjsq3eyNQtIQTVp-uZdhYihzEDB_w0AxBL8Mjx5HN5nIiuhcIb-6ejmMNL5HqqedM1RQ/exec",
      {
        method: "POST",
        body: new FormData(formRef.current),
      },
    )
      .then((res) => {
        alert("SUCCESSFULLY SUBMITTED");
        setSubmitStatus("success");
        setFormData({
          Name: "",
          Email: "",
          Phone: "",
          Designation: "",
          Organization: "",
          Message: "",
        });
      })
      .catch((error) => {
        console.error("Submission error:", error);
        setSubmitStatus("error");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <section className="service-hero-section">
      <div className="service-hero-wrapper">
        <div
          className="blue-hero-card"
          style={{
            backgroundImage: `linear-gradient(rgba(14, 25, 60, 0.5) 0%, rgba(14, 25, 60, 0.8) 100%), url('${
              heroBanner || "/images/home/hero-1.webp"
            }')`,
          }}
        >
          <div className="hero-content-left">
            <h1 className="hero-title">{title || "Business Transformation"}</h1>
            <p className="hero-description">
              {shortIntro && shortIntro.length > 0
                ? shortIntro[0]
                : "Rapid, evidence-backed diagnostics to identify operational, financial, and strategic gaps that limit growth."}
            </p>
            <div className="hero-btn-wrapper">
              <CustomButton
                as={NavLink}
                to={
                  "https://drive.google.com/file/d/1a18RfunC61u54Cp3ubT5FLI71F1NwlZl/view"
                }
                className="download-deck-btn"
              >
                Download Deck
              </CustomButton>
            </div>
          </div>
        </div>

        <div className="hero-form-wrapper">
          <div className="form-card">
            <h3>Let's Talk About Your Business Goals.</h3>
            <form
              className="service-hero-form"
              onSubmit={handleSubmit}
              ref={formRef}
              name="google-sheet"
            >
              <div className="form-group full">
                <label>Name*</label>
                <input
                  type="text"
                  name="Name"
                  value={formData.Name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className={errors.Name ? "error" : ""}
                />
                {errors.Name && (
                  <span className="error-text">{errors.Name}</span>
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Email*</label>
                  <input
                    type="email"
                    name="Email"
                    value={formData.Email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className={errors.Email ? "error" : ""}
                  />
                  {errors.Email && (
                    <span className="error-text">{errors.Email}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Phone no.*</label>
                  <input
                    type="tel"
                    name="Phone"
                    value={formData.Phone}
                    onChange={handleChange}
                    placeholder="Enter your phone no."
                    className={errors.Phone ? "error" : ""}
                  />
                  {errors.Phone && (
                    <span className="error-text">{errors.Phone}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Designation*</label>
                  <input
                    type="text"
                    name="Designation"
                    value={formData.Designation}
                    onChange={handleChange}
                    placeholder="Enter your designation"
                    className={errors.Designation ? "error" : ""}
                  />
                  {errors.Designation && (
                    <span className="error-text">{errors.Designation}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Organization Name*</label>
                  <input
                    type="text"
                    name="Organization"
                    value={formData.Organization}
                    onChange={handleChange}
                    placeholder="Enter organization name"
                    className={errors.Organization ? "error" : ""}
                  />
                  {errors.Organization && (
                    <span className="error-text">{errors.Organization}</span>
                  )}
                </div>
              </div>
              <div className="form-group full">
                <label>Your Message</label>
                <textarea
                  name="Message"
                  value={formData.Message}
                  onChange={handleChange}
                  placeholder="Enter your message"
                  rows="3"
                ></textarea>
              </div>

              {submitStatus === "success" && (
                <div className="submit-message success">
                  Form submitted successfully! We'll get back to you soon.
                </div>
              )}
              {submitStatus === "error" && (
                <div className="submit-message error">
                  Something went wrong. Please try again later.
                </div>
              )}

              <div className="form-submit-wrapper">
                <CustomButton
                  className="submit-btn"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Book a Free Consultation"}
                </CustomButton>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="service-stats-section" ref={statsRef}>
        <div className="service-stats-container">
          <div className="stat-card">
            <div className="stat-num-box"><NumberTicker value="50+" trigger={isStatsInView} /></div>
            <div className="stat-label">Clients Served</div>
          </div>
          <div className="stat-card">
            <div className="stat-num-box"><NumberTicker value="50+" trigger={isStatsInView} /></div>
            <div className="stat-label">Cumulative Years of Experience</div>
          </div>
          <div className="stat-card">
            <div className="stat-num-box"><NumberTicker value="10+" trigger={isStatsInView} /></div>
            <div className="stat-label">Sectors Served</div>
          </div>
        </div>
      </div>

      <div className="service-overview-subsection">
        <div className="service-overview-card">
          <div className="overview-left">
            <div className="overview-icon-container">
              <img
                src="/images/services/research.png"
                alt="Service Overview"
                className="overview-main-icon"
              />
            </div>
            <h4 className="overview-title">Service Overview</h4>
          </div>
          <div className="overview-right">
            {(Array.isArray(serviceOverview)
              ? serviceOverview
              : [
                  serviceOverview ||
                    "A targeted, data-driven review of your business across finance, operations, and strategy to create a clear roadmap for growth and resilience.",
                ]
            ).map((text, index) => (
              <div className="overview-item" key={index}>
                <div className="check-icon-wrapper">
                  <img src="/icons/service/service-icn.svg" alt="check" />
                </div>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceHero;
